import type { EChartsOption, SankeySeriesOption } from 'echarts';

import {
  FONT_SIZE,
  FormatMetric,
  toBaseOption,
  toTooltipHtml,
  toTooltipStyle,
} from './shared';

// A sankey: each dimension A term (left) flowing into the dimension B terms
// (right), as wide as the metric's value. Its nodes are named by their ids
// (unique across both sides, see toSankeyLinks) and labelled by their terms.
// A drill-down picks a source, greying the others.
const toSankeyOption = ({
  model,
  theme,
  formatMetric,
}: {
  model: StudioCharts.SankeyModel;
  theme: StudioCharts.Theme;
  formatMetric: FormatMetric;
}): EChartsOption => {
  const nodeById = new Map(model.nodes.map((node) => [node.id, node]));
  const nameOf = (id: string) => {
    const node = nodeById.get(id);
    return node ? node.name : id;
  };
  const format = (value: number) =>
    formatMetric({ metricId: model.metricId, value });
  const series: SankeySeriesOption = {
    type: 'sankey',
    id: model.seriesId,
    name: model.name,
    left: 8,
    right: 96,
    top: 8,
    bottom: 8,
    nodeGap: 8,
    // Laid out as it's given, as the Highcharts sankey: its nodes in their
    // order, not moved about to untangle the links, nor dragged
    layoutIterations: 0,
    draggable: false,
    data: model.nodes.map((node) => ({
      name: node.id,
      itemStyle: {
        color:
          node.isSource &&
          model.selectedKeys &&
          !model.selectedKeys.includes(node.name)
            ? theme.deselected
            : node.color,
      },
    })),
    links: model.links.map((link) => ({
      source: link.from,
      target: link.to,
      value: link.weight,
    })),
    // Each link in its source's colour, faint
    lineStyle: { color: 'source', opacity: 0.3 },
    label: {
      color: theme.textPrimary,
      fontSize: FONT_SIZE,
      formatter: ({ name }) => nameOf(name),
    },
    emphasis: { focus: 'adjacency' },
  };
  return {
    ...toBaseOption(theme),
    tooltip: {
      ...toTooltipStyle(theme),
      trigger: 'item',
      formatter: (params) => {
        const item = Array.isArray(params) ? params[0] : params;
        if (!item) {
          return '';
        }
        const data = item.data as { source?: string; target?: string };
        // A link, from its source to its target, or a node's total
        return item.dataType === 'edge' && data.source && data.target
          ? toTooltipHtml({
              title: `${nameOf(data.source)} → ${nameOf(data.target)}`,
              rows: [{ name: model.name, value: format(item.value as number) }],
            })
          : toTooltipHtml({
              title: nameOf(item.name),
              rows: [{ name: model.name, value: format(item.value as number) }],
            });
      },
    },
    series: [series],
  };
};

export default toSankeyOption;
